import type { ToolPart } from './ToolCallDisplay'

const CREATE_PRODUCT_TOOL_NAME = 'createProduct'

/**
 * Resolve a tool part's tool name regardless of whether it arrived as a typed
 * `tool-<name>` part or a `dynamic-tool` part. Mirrors the extraction in
 * `ToolCallDisplay` so callers see the same identity the dispatcher does.
 */
const getToolName = (part: ToolPart): string =>
  part.toolName ?? (part.type.startsWith('tool-') ? part.type.replace('tool-', '') : part.type)

const isCreateProductPart = (part: ToolPart): boolean => getToolName(part) === CREATE_PRODUCT_TOOL_NAME

/**
 * Shape the `createProduct` adapter emits for a single tool result:
 * `{ summary, data: { proposals: ProductProposal[] } }`. We only need the
 * proposals array here — the rest stays untyped so this helper does not
 * need to import any backend types.
 */
type CreateProductOutput = {
  summary?: string
  data?: { proposals?: unknown[] }
}

const extractProposals = (output: unknown): unknown[] => {
  if (typeof output !== 'object' || output === null) return []
  const proposals = (output as CreateProductOutput).data?.proposals
  return Array.isArray(proposals) ? proposals : []
}

/**
 * Collapse multiple sibling `createProduct` tool parts in a single assistant
 * message into one aggregated tool part whose `output.data.proposals[]` holds
 * every proposal across the run. This is the LLM-emit-fanout workaround:
 * when the model fires N separate `createProduct` calls in one turn instead
 * of one batched call, the chat would otherwise render N cards (one per
 * call) with one proposal each, AND mount N renderers in parallel. After
 * coalescing the user sees the same single grid they'd get from a batched
 * call.
 *
 * Rules:
 * - Non-`createProduct` parts are passed through in order, untouched.
 * - When the message has 2+ `createProduct` parts, ALL of them collapse into
 *   ONE synthetic part inserted at the position of the FIRST `createProduct`
 *   part — preserving relative ordering against the other tool calls.
 * - When the message has exactly 1 `createProduct` part, the input is
 *   returned unchanged (no synthetic envelope, no copy).
 * - The synthetic part inherits the first part's `toolCallId` (stable React
 *   key), `state`, and `input`. Its `output` is replaced with a fresh
 *   envelope carrying the combined proposals array; the per-part summaries
 *   are joined so the JSON fallback (if it ever renders) still reads sanely.
 */
export const coalesceCreateProductToolParts = (toolParts: ToolPart[]): ToolPart[] => {
  const createProductParts = toolParts.filter(isCreateProductPart)
  if (createProductParts.length < 2) return toolParts

  const allProposals = createProductParts.flatMap((part) => extractProposals(part.output))
  const summary = createProductParts
    .map((part) =>
      typeof part.output === 'object' && part.output !== null ? (part.output as CreateProductOutput).summary : undefined
    )
    .filter((line): line is string => Boolean(line))
    .join('\n')

  const firstPart = createProductParts[0]
  const mergedPart: ToolPart = {
    ...firstPart,
    output: { summary, data: { proposals: allProposals } },
  }

  const result: ToolPart[] = []
  let merged = false
  for (const part of toolParts) {
    if (!isCreateProductPart(part)) {
      result.push(part)
      continue
    }
    if (!merged) {
      result.push(mergedPart)
      merged = true
    }
    // Skip subsequent createProduct parts — they're folded into mergedPart.
  }
  return result
}
